import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

import "./CompanyInvoiceSettings.css";

const API_URL = "/api";

const emptyForm = {
  invoice_prefix: "FV",
  current_number: 1,
  resolution_number: "",
  resolution_date: "",
  valid_from: "",
  valid_until: "",
  authorized_from: "",
  authorized_to: "",
  default_due_days: 30,
  default_note: "",
  show_logo: true,
  show_payment_terms: true,
  is_active: true,
};

const toText = (value) => value ?? "";

const normalizeInvoiceSettings = (data = {}) => ({
  ...emptyForm,
  ...data,
  invoice_prefix: toText(data.invoice_prefix) || "FV",
  current_number: data.current_number ?? 1,
  resolution_number: toText(data.resolution_number),
  resolution_date: toText(data.resolution_date),
  valid_from: toText(data.valid_from),
  valid_until: toText(data.valid_until),
  authorized_from: data.authorized_from ?? "",
  authorized_to: data.authorized_to ?? "",
  default_due_days: data.default_due_days ?? 30,
  default_note: toText(data.default_note),
  show_logo: data.show_logo ?? true,
  show_payment_terms: data.show_payment_terms ?? true,
  is_active: data.is_active ?? true,
});

const getHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("company_token")}`,
});

const toOptionalNumber = (value) => {
  if (value === "" || value === null || value === undefined) {
    return null;
  }

  return Number(value);
};

export default function CompanyInvoiceSettings({
  readOnly = false,
}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(
        `${API_URL}/settings/invoice/me`,
        {
          headers: getHeaders(),
        }
      );

      setForm(normalizeInvoiceSettings(response.data));
    } catch (error) {
      console.error(error);

      toast.error(
        error.response?.data?.detail ||
          "No fue posible cargar la configuración de facturación."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setForm((previous) => ({
      ...previous,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (readOnly) {
      return;
    }

    const prefix = toText(form.invoice_prefix)
      .trim()
      .toUpperCase();

    if (!prefix) {
      toast.warning("El prefijo de factura es obligatorio.");
      return;
    }

    const authorizedFrom = toOptionalNumber(
      form.authorized_from
    );
    const authorizedTo = toOptionalNumber(
      form.authorized_to
    );
    const currentNumber = Number(form.current_number);

    if (
      authorizedFrom !== null &&
      authorizedTo !== null &&
      authorizedFrom > authorizedTo
    ) {
      toast.warning(
        "El consecutivo inicial autorizado no puede ser mayor al final."
      );
      return;
    }

    if (
      authorizedFrom !== null &&
      currentNumber < authorizedFrom
    ) {
      toast.warning(
        "El consecutivo actual no puede ser menor al inicio autorizado."
      );
      return;
    }

    if (
      authorizedTo !== null &&
      currentNumber > authorizedTo
    ) {
      toast.warning(
        "El consecutivo actual no puede superar el final autorizado."
      );
      return;
    }

    try {
      setSaving(true);

      const payload = {
        invoice_prefix: prefix,
        current_number: currentNumber,
        resolution_number:
          toText(form.resolution_number).trim() || null,
        resolution_date: form.resolution_date || null,
        valid_from: form.valid_from || null,
        valid_until: form.valid_until || null,
        authorized_from: authorizedFrom,
        authorized_to: authorizedTo,
        default_due_days: Number(
          form.default_due_days || 0
        ),
        default_note:
          toText(form.default_note).trim() || null,
        show_logo: form.show_logo,
        show_payment_terms: form.show_payment_terms,
        is_active: form.is_active,
      };

      const response = await axios.put(
        `${API_URL}/settings/invoice/me`,
        payload,
        {
          headers: getHeaders(),
        }
      );

      setForm(normalizeInvoiceSettings(response.data));

      toast.success(
        "Configuración de facturación actualizada."
      );
    } catch (error) {
      console.error(error);

      toast.error(
        error.response?.data?.detail ||
          "No fue posible guardar la configuración de facturación."
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="settings-loading">
        Cargando configuración de facturación...
      </div>
    );
  }

  return (
    <form
      className="invoice-settings-form"
      onSubmit={handleSubmit}
    >
      <div className="invoice-settings-heading">
        <div>
          <h3>Configuración de facturación</h3>
          <p>
            Define la resolución, rango autorizado y
            consecutivo de tus facturas.
          </p>
        </div>

        <span
          className={
            form.is_active
              ? "invoice-status active"
              : "invoice-status inactive"
          }
        >
          {form.is_active
            ? "Facturación activa"
            : "Facturación inactiva"}
        </span>
      </div>

      <fieldset
        className="invoice-settings-fieldset"
        disabled={readOnly}
      >
        <section className="invoice-settings-section">
          <h4>Numeración de facturas</h4>

          <div className="invoice-settings-grid">
            <div className="floating-field">
              <input
                id="invoice_prefix"
                name="invoice_prefix"
                value={form.invoice_prefix}
                onChange={handleChange}
                placeholder=" "
                maxLength={20}
                required
              />
              <label htmlFor="invoice_prefix">Prefijo</label>
            </div>

            <div className="floating-field">
              <input
                id="current_number"
                name="current_number"
                type="number"
                min="1"
                value={form.current_number}
                onChange={handleChange}
                placeholder=" "
                required
              />
              <label htmlFor="current_number">
                Próximo consecutivo
              </label>
            </div>

            <div className="floating-field">
              <input
                id="authorized_from"
                name="authorized_from"
                type="number"
                min="1"
                value={form.authorized_from}
                onChange={handleChange}
                placeholder=" "
              />
              <label htmlFor="authorized_from">
                Rango autorizado desde
              </label>
            </div>

            <div className="floating-field">
              <input
                id="authorized_to"
                name="authorized_to"
                type="number"
                min="1"
                value={form.authorized_to}
                onChange={handleChange}
                placeholder=" "
              />
              <label htmlFor="authorized_to">
                Rango autorizado hasta
              </label>
            </div>
          </div>

          <p className="invoice-preview">
            Próxima factura:
            <strong>
              {" "}
              {form.invoice_prefix || "FV"}-
              {String(form.current_number || 1).padStart(
                6,
                "0"
              )}
            </strong>
          </p>
        </section>

        <section className="invoice-settings-section">
          <h4>Resolución o autorización</h4>

          <div className="invoice-settings-grid">
            <div className="floating-field invoice-field-wide">
              <input
                id="resolution_number"
                name="resolution_number"
                value={form.resolution_number}
                onChange={handleChange}
                placeholder=" "
                maxLength={80}
              />
              <label htmlFor="resolution_number">
                Número de resolución
              </label>
            </div>

            <div className="floating-field">
              <input
                id="resolution_date"
                name="resolution_date"
                type="date"
                value={form.resolution_date}
                onChange={handleChange}
              />
              <label htmlFor="resolution_date">
                Fecha de resolución
              </label>
            </div>

            <div className="floating-field">
              <input
                id="valid_from"
                name="valid_from"
                type="date"
                value={form.valid_from}
                onChange={handleChange}
              />
              <label htmlFor="valid_from">Vigente desde</label>
            </div>

            <div className="floating-field">
              <input
                id="valid_until"
                name="valid_until"
                type="date"
                value={form.valid_until}
                onChange={handleChange}
              />
              <label htmlFor="valid_until">Vigente hasta</label>
            </div>
          </div>
        </section>

        <section className="invoice-settings-section">
          <h4>Condiciones del documento</h4>

          <div className="invoice-settings-grid">
            <div className="floating-field">
              <input
                id="default_due_days"
                name="default_due_days"
                type="number"
                min="0"
                max="365"
                value={form.default_due_days}
                onChange={handleChange}
                placeholder=" "
              />
              <label htmlFor="default_due_days">
                Días de vencimiento por defecto
              </label>
            </div>

            <div className="floating-field invoice-field-wide">
              <input
                id="default_note"
                name="default_note"
                value={form.default_note}
                onChange={handleChange}
                placeholder=" "
                maxLength={500}
              />
              <label htmlFor="default_note">
                Nota predeterminada en factura
              </label>
            </div>
          </div>

          <div className="invoice-switches">
            <label className="invoice-switch">
              <input
                type="checkbox"
                name="show_logo"
                checked={form.show_logo}
                onChange={handleChange}
              />
              <span>Mostrar logo de la empresa</span>
            </label>

            <label className="invoice-switch">
              <input
                type="checkbox"
                name="show_payment_terms"
                checked={form.show_payment_terms}
                onChange={handleChange}
              />
              <span>Mostrar condiciones de pago</span>
            </label>

            <label className="invoice-switch">
              <input
                type="checkbox"
                name="is_active"
                checked={form.is_active}
                onChange={handleChange}
              />
              <span>Habilitar emisión de facturas</span>
            </label>
          </div>
        </section>
      </fieldset>

      <div className="invoice-settings-actions">
        {!readOnly ? (
          <button type="submit" disabled={saving}>
            {saving
              ? "Guardando..."
              : "Guardar configuración"}
          </button>
        ) : (
          <span className="settings-view-only">
            Solo consulta
          </span>
        )}
      </div>
    </form>
  );
}